import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const TODAY = new Date().toISOString().slice(0, 10);

function pctColor(pct, closed) {
  if (closed) return { bg: '#f8fafc', text: '#cbd5e1', border: '#f1f5f9' };
  if (pct === 0)   return { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' };
  if (pct < 50)    return { bg: '#f0fdf4', text: '#15803d', border: '#86efac' };
  if (pct < 75)    return { bg: '#fefce8', text: '#92400e', border: '#fde68a' };
  if (pct < 90)    return { bg: '#fff7ed', text: '#c2410c', border: '#fdba74' };
  return             { bg: '#fef2f2', text: '#b91c1c', border: '#fca5a5' };
}

function pctLabel(pct, closed, reservations) {
  if (closed)          return 'Closed';
  if (reservations === 0) return 'Open';
  return `${pct}%`;
}

export default function Capacity() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays]       = useState(60);
  const [selected, setSelected] = useState(null); // date string
  const [dayResos, setDayResos] = useState([]);
  const [loadingResos, setLoadingResos] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.getCapacity(days).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener('seva:refresh', load);
    return () => window.removeEventListener('seva:refresh', load);
  }, [load]);

  const selectDay = async (date) => {
    setSelected(date);
    setLoadingResos(true);
    try {
      const resos = await api.getReservations({ date });
      setDayResos(resos.filter(r => r.status !== 'cancelled'));
    } catch (_) { setDayResos([]); } finally { setLoadingResos(false); }
  };

  // Group by month
  const months = [];
  let cur = null;
  for (const d of data) {
    const m = d.date.slice(0, 7);
    if (!cur || cur.key !== m) {
      cur = { key: m, label: new Date(d.date + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }), days: [] };
      months.push(cur);
    }
    cur.days.push(d);
  }

  // Leading blanks for first month
  const firstDate   = data[0]?.date;
  const leadingBlanks = firstDate
    ? new Date(firstDate + 'T12:00:00Z').getUTCDay()
    : 0;

  const selData = data.find(d => d.date === selected);

  function fmt(t) {
    const [h, m] = t.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  }

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Capacity</h1>
          <p style={s.sub}>How full each upcoming day is. Click any day to see its reservations.</p>
        </div>
        <div style={s.rangeBtns}>
          {[30, 60, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              style={{ ...s.rangeBtn, ...(days === d ? s.rangeBtnActive : {}) }}>
              {d} days
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={s.legend}>
        {[
          { label: 'Open', bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
          { label: '1–49%', bg: '#f0fdf4', border: '#86efac', text: '#15803d' },
          { label: '50–74%', bg: '#fefce8', border: '#fde68a', text: '#92400e' },
          { label: '75–89%', bg: '#fff7ed', border: '#fdba74', text: '#c2410c' },
          { label: '90–100%', bg: '#fef2f2', border: '#fca5a5', text: '#b91c1c' },
          { label: 'Closed', bg: '#f8fafc', border: '#f1f5f9', text: '#cbd5e1' },
        ].map(l => (
          <div key={l.label} style={s.legendItem}>
            <div style={{ ...s.legendSwatch, background: l.bg, border: `1px solid ${l.border}` }} />
            <span style={{ color: l.text }}>{l.label}</span>
          </div>
        ))}
      </div>

      <div style={s.layout}>
        {loading ? (
          <div style={{ color: '#64748b', padding: 40 }}>Loading…</div>
        ) : (
          <div style={s.calWrap}>
            {/* Day-of-week headers */}
            <div style={s.dowRow}>
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d =>
                <div key={d} style={s.dowCell}>{d}</div>
              )}
            </div>

            {months.map((month, mi) => (
              <div key={month.key}>
                <div style={s.monthLabel}>{month.label}</div>
                <div style={s.grid}>
                  {mi === 0 && Array.from({ length: leadingBlanks }).map((_, i) =>
                    <div key={`blank-${i}`} style={s.blank} />
                  )}
                  {month.days.map(d => {
                    const c       = pctColor(d.pct, d.closed);
                    const isToday = d.date === TODAY;
                    const isSel   = d.date === selected;
                    const dayNum  = parseInt(d.date.slice(8));
                    return (
                      <div
                        key={d.date}
                        onClick={() => !d.closed && selectDay(d.date)}
                        style={{
                          ...s.cell,
                          background: c.bg,
                          border: `1px solid ${isSel ? '#4f46e5' : isToday ? '#6366f1' : c.border}`,
                          boxShadow: isSel ? '0 0 0 2px #4f46e5' : isToday ? '0 0 0 2px #a5b4fc' : 'none',
                          cursor: d.closed ? 'default' : 'pointer',
                          opacity: d.date < TODAY ? 0.45 : 1,
                        }}
                      >
                        <div style={{ ...s.cellDay, color: isToday ? '#4f46e5' : '#64748b', fontWeight: isToday ? 800 : 500 }}>
                          {dayNum}
                        </div>
                        <div style={{ ...s.cellPct, color: c.text }}>
                          {pctLabel(d.pct, d.closed, d.reservations)}
                        </div>
                        {d.reservations > 0 && !d.closed && (
                          <div style={{ ...s.cellCount, color: c.text }}>
                            {d.reservations} bk
                          </div>
                        )}
                        {d.pct > 0 && !d.closed && (
                          <div style={s.bar}>
                            <div style={{ ...s.barFill, width: `${d.pct}%`, background: c.text }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Side panel */}
        {selected && (
          <div style={s.panel}>
            <div style={s.panelHead}>
              <div style={s.panelDate}>
                {new Date(selected + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })}
              </div>
              {selData && !selData.closed && (
                <div style={s.panelStats}>
                  <span style={s.panelStat}>{selData.covers} covers</span>
                  <span style={s.panelStat}>{selData.reservations} bookings</span>
                  <span style={{ ...s.panelStat, color: pctColor(selData.pct, false).text, fontWeight: 700 }}>
                    {selData.pct}% full
                  </span>
                </div>
              )}
              <button style={s.closeBtn} onClick={() => setSelected(null)}>×</button>
            </div>
            <div style={s.panelDivider} />
            {loadingResos ? (
              <div style={{ color: '#94a3b8', padding: '12px 0', fontSize: 13 }}>Loading…</div>
            ) : dayResos.length === 0 ? (
              <div style={{ color: '#94a3b8', padding: '16px 0', textAlign: 'center', fontSize: 13 }}>No reservations</div>
            ) : (
              dayResos.map(r => (
                <div key={r.id} style={s.resoRow}>
                  <div style={s.resoTime}>{fmt(r.time_slot)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={s.resoName}>{r.customer_name}</div>
                    <div style={s.resoMeta}>{r.party_size} guests · {r.table_label}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  h1:           { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
  sub:          { fontSize: 14, color: '#64748b', marginTop: 4 },
  rangeBtns:    { display: 'flex', gap: 6 },
  rangeBtn:     { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: '#475569', fontWeight: 500 },
  rangeBtnActive:{ background: '#4f46e5', borderColor: '#4f46e5', color: '#fff' },

  legend:       { display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' },
  legendItem:   { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 },
  legendSwatch: { width: 16, height: 16, borderRadius: 4 },

  layout:       { display: 'flex', gap: 20, alignItems: 'flex-start' },
  calWrap:      { flex: 1, minWidth: 0 },

  dowRow:       { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 },
  dowCell:      { fontSize: 11, fontWeight: 700, color: '#94a3b8', textAlign: 'center', padding: '4px 0', textTransform: 'uppercase', letterSpacing: '0.06em' },
  monthLabel:   { fontSize: 13, fontWeight: 700, color: '#334155', margin: '16px 0 6px' },

  grid:         { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 },
  blank:        { height: 76 },
  cell:         { borderRadius: 10, padding: '8px 9px', height: 76, display: 'flex', flexDirection: 'column', transition: 'box-shadow 0.15s, border-color 0.15s', overflow: 'hidden' },
  cellDay:      { fontSize: 13, marginBottom: 2 },
  cellPct:      { fontSize: 14, fontWeight: 800, lineHeight: 1 },
  cellCount:    { fontSize: 10, marginTop: 2, opacity: 0.8 },
  bar:          { marginTop: 'auto', height: 3, background: 'rgba(0,0,0,0.07)', borderRadius: 2, overflow: 'hidden' },
  barFill:      { height: '100%', borderRadius: 2, opacity: 0.5, transition: 'width 0.3s' },

  panel:        { width: 260, flexShrink: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: '18px 20px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', maxHeight: 560, overflow: 'auto' },
  panelHead:    { display: 'flex', flexDirection: 'column', gap: 6 },
  panelDate:    { fontSize: 15, fontWeight: 700, color: '#0f172a', paddingRight: 32, lineHeight: 1.3, position: 'relative' },
  panelStats:   { display: 'flex', gap: 8, flexWrap: 'wrap' },
  panelStat:    { fontSize: 12, color: '#64748b', background: '#f8fafc', borderRadius: 6, padding: '3px 8px' },
  closeBtn:     { position: 'absolute', right: 20, top: 18, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8', lineHeight: 1, padding: 0 },
  panelDivider: { height: 1, background: '#f1f5f9', margin: '12px 0' },
  resoRow:      { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 0', borderBottom: '1px solid #f1f5f9' },
  resoTime:     { fontSize: 11, fontWeight: 700, color: '#64748b', width: 58, flexShrink: 0, paddingTop: 1 },
  resoName:     { fontSize: 13, fontWeight: 600, color: '#0f172a' },
  resoMeta:     { fontSize: 11, color: '#94a3b8', marginTop: 2 },
};
